import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { authService } from '@/services/auth/authService';
import { useDebounce } from '@/hooks/useDebounce';
import { createMruStore } from '@/lib/mruStore';

/**
 * The one fetch path behind every lookup picker (lookup spec §6): the generated
 * `GET /api/{module}/{entity}/lookup` endpoint, one debounce and one react-query key policy.
 * Pickers own presentation only; anything added to fetching (recently used, dependent filters)
 * is added here once.
 */

/** A lookup DTO: the id plus the flat display/search fields the endpoint projects. */
export interface LookupOption {
  id: string;
  [key: string]: string | number | boolean | null | undefined;
}

interface LookupPage {
  items: LookupOption[];
  totalCount: number;
}

const DEBOUNCE_MS = 300;

function lookupUrl(module: string, entity: string): string {
  return `/api/${module.toLowerCase()}/${entity.toLowerCase()}/lookup`;
}

/** Tenant is part of every key: one browser serves several tenants, and a cached page must not cross. */
function lookupQueryKey(module: string, entity: string, ...rest: unknown[]): unknown[] {
  return ['lookupOptions', module.toLowerCase(), entity.toLowerCase(), authService.getTenantId(), ...rest];
}

/** Filter values by query parameter name (the looked-up entity's field), lookup spec §5. */
export type LookupFilterValues = Record<string, string>;

/** A dependent-lookup filter as the schema declares it (`x-lookup.filters`). */
export interface LookupFilterConfig {
  target: string;
  fromField: string;
  when?: 'required' | 'optional';
}

export interface ResolvedLookupFilters {
  /** Query parameters for the sources that have a value. */
  values: LookupFilterValues;
  /** `fromField`s of required filters whose source is still empty — the control waits for them. */
  missingRequired: string[];
}

function readPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((cur, key) => (cur == null ? undefined : (cur as Record<string, unknown>)[key]), obj);
}

/**
 * Resolve each filter's source from the form data (lookup spec §5.3): the sibling in the same
 * row first — the object that holds the field at `fieldPath` — then the form root. Inside a
 * child collection the root would read the wrong object.
 */
export function resolveLookupFilters(
  filters: LookupFilterConfig[] | undefined,
  formData: unknown,
  fieldPath: string,
): ResolvedLookupFilters {
  const rowPath = fieldPath.split('.').slice(0, -1).join('.');
  const row = rowPath ? readPath(formData, rowPath) : formData;
  const values: LookupFilterValues = {};
  const missingRequired: string[] = [];
  for (const f of filters ?? []) {
    const raw = readFilterSource(formData, row, rowPath, f.fromField);
    if (!isEmptySource(raw)) values[f.target] = String(raw);
    else if ((f.when ?? 'required') === 'required') missingRequired.push(f.fromField);
  }
  return { values, missingRequired };
}

/** The source's value in the row, else at the form root (only when the row is not the root). */
function readFilterSource(formData: unknown, row: unknown, rowPath: string, fromField: string): unknown {
  return readPath(row, fromField) ?? (rowPath ? readPath(formData, fromField) : undefined);
}

const isEmptySource = (raw: unknown) => raw === undefined || raw === null || raw === '';

// ── Recently used (lookup spec §4) ──

/** Shown in the "Recently used" group at most. */
const RECENT_SHOWN = 5;
/**
 * Kept per slot: more than are shown, so that under a dependent filter — which admits only some of
 * a slot's history — the group can still fill up with values that are valid right now.
 */
const RECENT_KEPT = 20;

/**
 * Per field slot (`x-lookup.recentlyUsedSlot`, `{module}.{Entity}.{fieldPath}`), per tenant: the
 * stored keys (id, or the code a codeLookup stores) the user last picked, newest first. Keys only —
 * an entry is a candidate, shown only after `keys=` has re-read it under the active filters, and
 * that read brings the current label (lookup spec §4.2, §4.4).
 */
const recentLookupKeys = createMruStore<string>({
  prefix: 'nexo.lookupRecent',
  max: RECENT_KEPT,
  isEntry: (x): x is string => typeof x === 'string' && x !== '',
  isSame: (a, b) => a === b,
});

const NO_KEYS: string[] = [];

/** Record a picked value as the slot's most recent (lookup spec §4.5: on selection, not on save). */
export function recordRecentLookup(slot: string, key: unknown): void {
  if (key === undefined || key === null || key === '') return;
  recentLookupKeys.record(String(key), slot);
}

function useRecentLookupKeys(slot: string | undefined): string[] {
  return useSyncExternalStore(recentLookupKeys.subscribe, () => (slot ? recentLookupKeys.get(slot) : NO_KEYS));
}

export interface ArrangedLookupOptions {
  options: LookupOption[];
  /** How many of `options`, from the start, form the "Recently used" group. */
  recentCount: number;
}

/**
 * Put a slot's recently used values first (lookup spec §4.3).
 *
 * With `validatedRecent` — the `keys=` read for an empty query — the group is those options in
 * recency order, at most five, followed by the page without them. Without it (a typed query) the
 * page stays one group: its recently used matches move to the top in recency order, the rest keep
 * the server's order. Only options the endpoint returned are ever shown, so a recent key that is
 * deleted, another tenant's or outside an active filter never appears.
 */
export function arrangeRecentFirst(
  page: LookupOption[],
  recentKeys: string[],
  valueField: string,
  validatedRecent?: LookupOption[],
): ArrangedLookupOptions {
  const keyOf = (o: LookupOption) => String(o[valueField]);
  if (validatedRecent) {
    // `keys=` matches a key against the id and the code: confirm it on the field that was stored.
    const recent = recentKeys
      .map((k) => validatedRecent.find((o) => keyOf(o) === k))
      .filter((o): o is LookupOption => o !== undefined)
      .slice(0, RECENT_SHOWN);
    const shown = new Set(recent.map(keyOf));
    return { options: [...recent, ...page.filter((o) => !shown.has(keyOf(o)))], recentCount: recent.length };
  }
  const rank = new Map(recentKeys.map((k, i) => [k, i]));
  const floated = page.filter((o) => rank.has(keyOf(o))).sort((a, b) => rank.get(keyOf(a))! - rank.get(keyOf(b))!);
  return { options: [...floated, ...page.filter((o) => !rank.has(keyOf(o)))], recentCount: 0 };
}

export interface UseLookupOptionsArgs {
  module: string;
  entity: string;
  /** The typed search text; debounced here, not by the caller. Empty returns the first page. */
  query: string;
  pageSize: number;
  /** Dependent-lookup filter values; part of the cache key, so a parent change refetches. */
  filters?: LookupFilterValues;
  /** False suspends fetching (e.g. while the dropdown is closed). Default true. */
  enabled?: boolean;
  /**
   * The field's recently-used slot (`x-lookup.recentlyUsedSlot`). Absent — the field opted out, or
   * the caller has no slot — means no recently-used behaviour at all.
   */
  recentSlot?: string;
  /** The option field the value is stored from: `id` (default), or a codeLookup's code field. */
  valueField?: string;
}

export interface LookupOptionsResult extends ArrangedLookupOptions {
  loading: boolean;
  /** Record a picked option in the slot's history; a no-op without a slot. */
  recordRecent: (option: LookupOption) => void;
}

/**
 * Search the lookup endpoint. The previous page stays visible while the next query loads, so the
 * list does not flash empty between keystrokes. With a `recent` slot, an empty query also re-reads
 * the slot's history through `keys=` under the same filters, and the options come back with the
 * valid recent values first (`arrangeRecentFirst`).
 */
export function useLookupOptions({
  module, entity, query, pageSize, filters, enabled = true, recentSlot, valueField = 'id',
}: UseLookupOptionsArgs): LookupOptionsResult {
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);
  const filterValues = filters ?? {};
  const { data, isFetching } = useQuery<LookupPage>({
    queryKey: lookupQueryKey(module, entity, 'search', pageSize, debouncedQuery, filterValues),
    queryFn: async () => {
      const response = await apiClient.get(lookupUrl(module, entity), {
        params: { filter: debouncedQuery, maxResultCount: pageSize, ...filterValues },
      });
      return response.data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });

  const recentKeys = useRecentLookupKeys(recentSlot);
  const wantsRecentGroup = debouncedQuery === '' && recentKeys.length > 0;
  const validated = useQuery<LookupOption[]>({
    // The key set, not its order: picking a value already in the history only reorders, and the
    // order is applied from the store — no refetch for that.
    queryKey: lookupQueryKey(module, entity, 'recent', valueField, [...recentKeys].sort(), filterValues),
    queryFn: () => fetchLookupByKeys(module, entity, recentKeys, filterValues),
    enabled: enabled && wantsRecentGroup,
  });

  const recordRecent = useCallback(
    (option: LookupOption) => {
      if (recentSlot) recordRecentLookup(recentSlot, option[valueField]);
    },
    [recentSlot, valueField],
  );

  // Without a slot the history is empty, so this is the page as returned.
  const arranged = arrangeWithHistory(data?.items ?? [], recentKeys, valueField, wantsRecentGroup, validated);
  return { ...arranged, loading: isFetching || validated.isFetching, recordRecent };
}

/**
 * `arrangeRecentFirst` for the hook's query state. While the history's `keys=` read is pending
 * nothing is shown — not the page with the group then pushing it down, and never an unvalidated
 * value at the top. If that read fails, the page shows without a group.
 */
function arrangeWithHistory(
  page: LookupOption[],
  recentKeys: string[],
  valueField: string,
  wantsRecentGroup: boolean,
  validated: { data?: LookupOption[]; isError: boolean },
): ArrangedLookupOptions {
  if (!wantsRecentGroup) return arrangeRecentFirst(page, recentKeys, valueField);
  if (validated.isError) return { options: page, recentCount: 0 };
  if (!validated.data) return { options: [], recentCount: 0 };
  return arrangeRecentFirst(page, recentKeys, valueField, validated.data);
}

/**
 * Exact-key read (`keys=`, lookup spec §4.4): resolve stored ids or codes to lookup options.
 * Keys the endpoint no longer returns — deleted, another tenant's, outside a filter — are simply
 * absent from the result.
 */
export async function fetchLookupByKeys(
  module: string,
  entity: string,
  keys: string[],
  filters?: LookupFilterValues,
): Promise<LookupOption[]> {
  if (keys.length === 0) return [];
  const response = await apiClient.get(lookupUrl(module, entity), { params: { keys: keys.join(','), ...filters } });
  return response.data.items ?? [];
}

/**
 * Resolve one stored value to its lookup option, or null when it does not resolve. `valueField` is
 * the option field the value is stored from (`id`, or a codeLookup's code field) — the endpoint
 * matches a key against the id and the code, so the match is confirmed on the field that was stored.
 */
export function useLookupByKey(module: string, entity: string, key: string | null, valueField: string, enabled = true) {
  return useQuery<LookupOption | null>({
    queryKey: lookupQueryKey(module, entity, 'key', valueField, key),
    queryFn: async () =>
      (await fetchLookupByKeys(module, entity, [key!])).find((item) => item[valueField] === key) ?? null,
    enabled: enabled && !!key,
  });
}

// ── Dependent filters: clear on change (lookup spec §5.4) ──

/**
 * Lookup spec §5.4: when a dependent-lookup filter changes and the current value is no longer among
 * the options it allows, clear it and say why. Only a proven exclusion clears — the value is read
 * back through `keys=` under the new filter — never a value merely not on the visible page. A
 * required source that is now empty is proof on its own: it admits no value, and the save-side
 * check rejects one (§5.5). Used by the form picker and the grid cell alike.
 */
export function useClearWhenFilterExcludes({
  module, entity, value, valueField, filters, sourceMissing = false, onCleared,
}: {
  module: string;
  entity: string;
  value: string | null;
  valueField: string;
  filters?: LookupFilterValues;
  /** A required filter's source is empty (`ResolvedLookupFilters.missingRequired` is not). */
  sourceMissing?: boolean;
  onCleared: () => void;
}) {
  const [cleared, setCleared] = useState(false);
  const filtersKey = JSON.stringify([filters ?? {}, sourceMissing]);
  const previousKey = useRef(filtersKey);
  const latest = useRef({ value, onCleared });
  latest.current = { value, onCleared };

  useEffect(() => {
    if (previousKey.current === filtersKey) return;
    previousKey.current = filtersKey;
    const current = latest.current.value;
    if (!current) return;
    const clear = () => {
      latest.current.onCleared();
      setCleared(true);
    };
    if (sourceMissing) {
      clear();
      return;
    }
    if (!filters) return;
    let cancelled = false;
    fetchLookupByKeys(module, entity, [current], filters)
      .then((items) => {
        // The user may have picked another value while the read was out: that one came from the
        // new filter's own options, and the answer here is about a value no longer in the field.
        if (cancelled || latest.current.value !== current) return;
        if (items.some((item) => item[valueField] === current)) return;
        clear();
      })
      .catch(() => { /* leave the value; the save-side rule is the enforcement */ });
    return () => { cancelled = true; };
    // filters and sourceMissing are represented by filtersKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, module, entity, valueField]);

  return { cleared, dismiss: () => setCleared(false) };
}
